import {
    WorksheetSchema,
    WorksheetSchemaField,
    WorksheetSchemaListItem,
    WorksheetSchemaProperty,
    WorksheetSchemaSection,
    WorksheetSchemaTableRow,
} from '../../../../types/custom-worksheet-v2';
import { Calculation, Worksheet } from '../../../../types/worksheet';
import { SchemaState } from './store';
import {
    getField,
    getFieldCount,
    getProperty,
    getSection,
    isList,
    isSectionGroup,
    filterIncompleteProperties,
    searchSchema,
    isField,
    isTable,
    isSection,
    isListItem,
} from './utils';

type SelectSchemaProps = {
    state: SchemaState;
    filter?: 'all' | 'remaining';
    search?: string;
};

export function selectIsDirty(state: SchemaState): boolean {
    return state.schema.dirty;
}

/**
 * Select schema with optional filter and search applied
 *
 * @returns WorksheetSchema
 */
export function selectSchema({ state, filter = 'all', search }: SelectSchemaProps): WorksheetSchema {
    if (!state.schema.value) {
        throw new Error('Please init a schema.');
    }

    if (filter === 'all') {
        // If search query is provided, return schema properties with matching fields
        if (search) {
            return searchSchema(state.schema.value, search, state.schema.showHidden);
        }

        return state.schema.value;
    }

    const schema = state.schema.value;
    const _schema: WorksheetSchema = { version: schema.version, properties: {}, reviewConditions: [] };

    for (const key in schema.properties) {
        const property = schema.properties[key];

        if (isList(property)) {
            const { remaining } = getFieldCount(property, state.schema.showHidden);

            if (remaining > 0) {
                const listItems: WorksheetSchemaListItem[] = [];
                for (const listItem of property.properties) {
                    const { remaining } = getFieldCount(listItem, state.schema.showHidden);

                    if (remaining > 0) {
                        listItems.push({ ...listItem, properties: filterIncompleteProperties(listItem, state.schema.showHidden) });
                    }
                }
                _schema.properties[key] = { ...property, properties: listItems };
            }
        }

        if (isTable(property)) {
            const { remaining } = getFieldCount(property, state.schema.showHidden);

            if (remaining > 0) {
                const rows: WorksheetSchemaTableRow[] = [];
                _schema.properties[key] = { ...property, properties: rows };
            }
        }

        if (isSectionGroup(property)) {
            const { remaining } = getFieldCount(property, state.schema.showHidden);
            if (remaining > 0) {
                _schema.properties[key] = {
                    ...property,
                    properties: filterIncompleteProperties(property, state.schema.showHidden),
                };
            }
        } else if (isSection(property)) {
            const { remaining } = getFieldCount(property, state.schema.showHidden);
            if (remaining > 0) {
                const _props: { [key: string]: WorksheetSchemaField } = {};

                for (const key in property.properties) {
                    const field = property.properties[key];
                    if (field.value === null) {
                        _props[key] = field;
                    }
                }
                _schema.properties[key] = { ...property, properties: _props };
            }
        } else if (isField(property)) {
            if (property.value === null) {
                _schema.properties[key] = property;
            }
        }
    }

    return _schema;
}

export function selectCalculation(state: SchemaState): Calculation {
    return state.schema.calculation;
}
export function selectWorksheet(state: SchemaState): Worksheet | undefined {
    return state.schema.worksheet;
}

/**
 * Select schema section using id
 *
 * @returns WorksheetSchemaSection | null
 */
export function selectSection(id: string): (state: SchemaState) => WorksheetSchemaSection | null {
    return (state): WorksheetSchemaSection | null => {
        if (!state.schema.value) {
            return null;
        }
        return getSection(state.schema.value, id);
    };
}

/**
 * Select schema field using id
 *
 * @returns WorksheetSchemaField | null
 */
export function selectField(id: string): (state: SchemaState) => WorksheetSchemaField | null {
    return (state): WorksheetSchemaField | null => {
        if (!state.schema.value) {
            return null;
        }
        return getField(state.schema.value, id);
    };
}

/**
 * Select list item that is currently being renamed
 *
 * @returns Id for list item that's currently being renamed
 */
export function selectRenamingListItem(state: SchemaState): string | undefined {
    return state.schema.renamingPropertyId;
}

/**
 * Select field with value currently being edited
 *
 * @returns Id for field with value currently being edited
 */
export function selectCurrentEditingField(state: SchemaState): string | undefined {
    return state.schema.currentEditingField;
}

/**
 * Select field that is currently being focused
 *
 * @returns Id for field that's currently being focused
 */
export function selectCurrentFocusField(state: SchemaState): { id: string; playAnimation: boolean } | undefined {
    return state.schema.currentFocusFieldId;
}

/**
 * Select whether schema form should be collapsed
 *
 * @returns True if all sections should be collapsed, false if all sections should be expanded
 */
export function selectCollapseAll(state: SchemaState): boolean | undefined {
    return state.schema.collapseAll;
}

export function selectShowHidden(state: SchemaState): boolean {
    return state.schema.showHidden ?? false;
}

/**
 * Get all other list item names within the same list as the list item
 *
 * @param listItemId List item id
 * @returns List item names
 */
export function selectListItemNames(property: WorksheetSchemaProperty): (state: SchemaState) => string[] {
    return (state): string[] => {
        if (!isListItem(property)) {
            return [];
        }

        const listId = (property.id.split('.') || []).slice(0, -1).join('.');
        const list = getProperty(state.schema.value, listId);
        if (!list || !isList(list)) {
            return [];
        }
        const listProperties = list.properties as WorksheetSchemaListItem[];

        return listProperties.map((p) => p.name.toLowerCase());
    };
}
